import { MenuItem } from './menu.service';

/**
 * Relevant menu icons (owner directive 2026-07-14: "Add your suggested menu
 * icons (relevant) shows on each menu and sub menu items").
 *
 * FontAwesome 4.7.0 is the only icon font loaded (see styles.scss ->
 * utils/font-awesome), so every value here is a valid FA4 glyph name WITHOUT
 * the `fa-` prefix (templates render `<i class="fa fa-{{icon}}">`). FA5/6-only
 * names (om, pray, receipt, coins, chart-line, boxes, cash-register ...) are
 * deliberately avoided.
 *
 * Resolution order (see resolveMenuIcon):
 *   1. an explicit non-empty `icon` already on the MenuItem wins;
 *   2. the first keyword whose regex matches "<id> <route> <text>" (most
 *      specific patterns are listed first);
 *   3. the item's group (mega-menu column / parent head) fallback;
 *   4. a generic default.
 * This keeps every one of the ~144 icon-less items covered without hand-editing
 * the large app.menu.ts data file, and new report screens get a sensible icon
 * for free from their name.
 */

// [pattern, FA4 glyph] — first match wins, so order matters.
const KEYWORD_ICONS: Array<[RegExp, string]> = [
    // -- top-level heads --
    [/\b(home|dashboard)\b/, 'home'],
    [/administration/, 'sitemap'],
    [/transaction/, 'exchange'],
    [/\bhelp\b/, 'question-circle'],

    // -- pooja / temple worship --
    [/star.*(pooja|wise)/, 'star'],
    [/monthly.*pooja|pooja.*monthly|devaswom/, 'calendar'],
    [/(pooja.*)?category/, 'th-list'],
    [/per.*pooja|pooja.*item/, 'list-alt'],
    [/manual.*entry/, 'pencil-square-o'],
    [/registered.*devotee|personal.*id.*pooja/, 'address-card-o'],
    [/\bdevotee\b/, 'user-circle'],
    [/vazhipad.*head|pooja.*head/, 'star-o'],
    [/vazhipad|pooja.*booking|pooja.*list|booking/, 'sun-o'],
    [/pooja.*summary/, 'list-alt'],
    [/pooja/, 'sun-o'],
    [/nadavaravu/, 'gift'],
    [/postage/, 'envelope-o'],
    [/avakasi|vihitham/, 'handshake-o'],

    // -- money movement / transactions --
    [/pooja.*stall|\bsale\b|stall/, 'shopping-basket'],
    [/expense/, 'money'],
    [/\bincome\b/, 'inr'],
    [/salary|payroll|attendance|employee/, 'users'],
    [/membership|member/, 'id-card-o'],
    [/auditorium|hall/, 'building'],
    [/bank/, 'university'],
    [/remittance/, 'exchange'],
    [/store.*pass|issue.*detail/, 'clipboard'],
    [/store.*transfer|stock.*transfer/, 'retweet'],
    [/(material.*)?refund|damage|return/, 'undo'],
    [/cheque/, 'money'],
    [/\bcard\b|\bpos\b|payment.*mode/, 'credit-card'],
    [/identified.*payment/, 'check-square-o'],
    [/journal|payment/, 'book'],
    [/online.*(pooja.*)?enquiry|online.*collection/, 'globe'],
    [/enquiry/, 'search'],

    // -- reports / audit / accounts --
    [/trial.*balance/, 'balance-scale'],
    [/balance.*sheet/, 'balance-scale'],
    [/income.*(expense|expenditure)|receipt.*payment/, 'exchange'],
    [/general.*ledger|ledger/, 'book'],
    [/cash.*book/, 'book'],
    [/bank.*book/, 'book'],
    [/day.*book/, 'calendar-o'],
    [/voucher/, 'file-text-o'],
    [/vendor.*statement|supplier/, 'user'],
    [/cancelled/, 'ban'],
    [/partial.*payment|due/, 'clock-o'],
    [/payable/, 'arrow-circle-up'],
    [/receivable/, 'arrow-circle-down'],
    [/collection.*summary|sale.*summary|summary/, 'pie-chart'],
    [/collection/, 'inr'],
    [/purchase.*register|purchase/, 'shopping-cart'],
    [/invoice/, 'file-text-o'],
    [/(current|today).*stock|stock.*(report|statement|consumption)|\bstock\b/, 'cubes'],
    [/register/, 'list-alt'],
    [/report|statement/, 'bar-chart-o'],

    // -- administration / masters --
    [/account.*head|\bhead\b/, 'sitemap'],
    [/date.*configuration|configuration/, 'calendar-check-o'],
    [/vendor|customer/, 'handshake-o'],
    [/product/, 'cube'],
    [/\buser\b|access.*right/, 'user-plus'],

    // -- help --
    [/keyboard/, 'keyboard-o'],
    [/manglish|layout|language/, 'language'],
    [/licen[cs]e/, 'certificate'],
    [/welcome/, 'info-circle'],
    [/about/, 'info-circle'],
];

// Mega-menu column / parent-head fallbacks (keyed on lower-cased group label).
const GROUP_ICONS: { [key: string]: string } = {
    pooja: 'sun-o',
    expense: 'money',
    audit: 'bar-chart-o',
    administraion: 'cog',   // note: the data file spells it "Administraion"
    administration: 'cog',
    services: 'exchange',
    report: 'file-text-o',
    reports: 'bar-chart-o',
    transaction: 'exchange',
    'stock-reports': 'cubes',
    stock: 'cubes',
    help: 'question-circle',
};

const DEFAULT_ICON = 'angle-right';

/** True when a data-supplied icon is present and usable. */
function hasExplicitIcon(item: MenuItem | null | undefined): boolean {
    return !!item && typeof item.icon === 'string' && item.icon.trim().length > 0;
}

/**
 * Pick a relevant FA4 glyph (no `fa-` prefix) for a menu/sub-menu item.
 * `group` is an optional column title / parent-head id used as a fallback.
 */
export function resolveMenuIcon(item: MenuItem | null | undefined, group?: string): string {
    if (hasExplicitIcon(item)) {
        return item!.icon.trim();
    }
    const hay = `${item?.id || ''} ${item?.route || ''} ${item?.text || ''}`.toLowerCase();
    for (const [pattern, icon] of KEYWORD_ICONS) {
        if (pattern.test(hay)) {
            return icon;
        }
    }
    const groupKey = (group || item?.mmgroupBy || item?.groupBy || '').toLowerCase().trim();
    if (groupKey && GROUP_ICONS[groupKey]) {
        return GROUP_ICONS[groupKey];
    }
    return DEFAULT_ICON;
}

/** Icon for a mega-menu column header (Pooja / Expense / Audit / ...). */
export function resolveGroupIcon(title: string | null | undefined): string {
    const key = (title || '').toLowerCase().trim();
    return GROUP_ICONS[key] || 'folder-open-o';
}
